import { useState, useEffect } from "react";
import { register, login } from "../services/userService";
import { useNavigate } from "react-router-dom";
import { useUser } from "../contexts/UserContext";
import { useModal } from "../contexts/ModalContext";


interface FormInput {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  successMessage?: string;
}

interface FormError {
  name?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
}
type RegisterProps = {
  onClose?: () => void;
};
const Register: React.FC<RegisterProps> = ({ onClose }) => {
  const navigate = useNavigate();

  const [formInput, setFormInput] = useState<FormInput>({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [formError, setFormError] = useState<FormError>({});
  const [submitted, setSubmitted] = useState(false);
  const { login: userLogin } = useUser();
  const { openModal } = useModal();

  useEffect(() => {
    if (localStorage.getItem("user")) {
      navigate("/dashboard");
    }
  }, [navigate]);

  const handleUserInput = (name: string, value: string) => {
    setFormInput((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = (): FormError => {
    const errors: FormError = {};

    if (!formInput.name.trim()) {
      errors.name = "Username is required.";
    } else if (formInput.name.length > 30) {
      errors.name = "Username cannot exceed 30 characters.";
    } else if (formInput.name.length < 5) {
      errors.name = "Username must be at least 5 characters.";
    }

    if (!formInput.email.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formInput.email)) {
      errors.email = "Invalid email format.";
    }

    if (!formInput.password.trim()) {
      errors.password = "Password is required.";
    } else if (formInput.password.length < 8) {
      errors.password = "Password must be at least 8 characters.";
    } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/.test(formInput.password)) {
      errors.password = "Password must contain uppercase, lowercase, and digit.";
    }

    if (formInput.confirmPassword !== formInput.password) {
      errors.confirmPassword = "Passwords do not match.";
    }

    return errors;
  };

  const serverValidationResult = (
    errorsList: { code: string; description: string }[]
  ): FormError => {
    const errors: FormError = {};
    errorsList.forEach(({ code, description }) => {
      if (code.includes("USER_NAME")) errors.name = description;
      if (code.includes("USER_EMAIL")) errors.email = description;
      if (code.includes("USER_PASSWORD")) errors.password = description;
    });
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const errors = validateForm();
    setFormError(errors);
    if (Object.keys(errors).length > 0) return;

    if (submitted) return;
    setSubmitted(true);

    try {
      await register(formInput.name, formInput.email, formInput.password);
      setFormInput((prev) => ({
        ...prev,
        successMessage: "Registration successful!  Automatically logging in...",
      }));
      setFormError({});

      window.alert("Registration successful! Automatically logging in...");

      //login
      const userData = await login(formInput.email, formInput.password);      
      if (userData){
        userLogin(userData);
        if (onClose) onClose();
      }
    } catch (data: any) {
      if (Array.isArray(data.errors)) {
        const apiErrors = serverValidationResult(data.errors);
        setFormError(apiErrors);
      } else {
        alert("Registration failed. Please check your credentials.");
      }
      setSubmitted(false);
    }
  };

  return (
    <form className="custom-form" onSubmit={handleSubmit}>
      <div className="field-wrapper">
        <label htmlFor="name">Name</label>
        <input
          id="name"
          name="name"
          type="text"
          value={formInput.name}
          onChange={(e) => handleUserInput(e.target.name, e.target.value)}
          placeholder="Name"
          required
        />
        {formError.name && <p className="error-message">{formError.name}</p>}
      </div>

      <div className="field-wrapper">
        <label htmlFor="email">Email</label>
        <input
          id="email"
          name="email"
          type="email"
          value={formInput.email}
          onChange={(e) => handleUserInput(e.target.name, e.target.value)}
          placeholder="Email"
          required
        />
        {formError.email && <p className="error-message">{formError.email}</p>}
      </div>

      <div className="field-wrapper">
        <label htmlFor="password">Password</label>
        <input
          id="password"
          name="password"
          type="password"
          value={formInput.password}
          onChange={(e) => handleUserInput(e.target.name, e.target.value)}
          placeholder="Password"
          required
        />
        {formError.password && <p className="error-message">{formError.password}</p>}
      </div>

      <div className="field-wrapper">
        <label htmlFor="confirmPassword">Confirm Password</label>
        <input
          id="confirmPassword"
          name="confirmPassword"
          type="password"
          value={formInput.confirmPassword}
          onChange={(e) => handleUserInput(e.target.name, e.target.value)}
          placeholder="Confirm Password"
          required
        />
        {formError.confirmPassword && (
          <p className="error-message">{formError.confirmPassword}</p>
        )}
      </div>

      {formInput.successMessage && (
        <p className="text-sm text-green-600 text-center">
          {formInput.successMessage}
        </p>
      )}

      <button
        type="submit"
        disabled={submitted}
        className="custom-btn w-full"
      >
        {submitted ? "Registering..." : "REGISTER"}
      </button>

      <p className="text-sm text-gray-500 text-center mt-4">
        Already have an account?{" "}
        <button
          onClick={(e: React.FormEvent) => {
            e.preventDefault();
            openModal("login");
          }}
          className="text-primary hover:underline"
        >
          Sign In
        </button>
      </p>
    </form>
  );
};

export default Register;